<section
  class="address-form-section"
  [formGroupName]="sectionKey()"
  [attr.aria-labelledby]="sectionTitleId()"
>
  <div class="address-form-section__header">
    <div class="address-form-section__heading">
      <div [id]="sectionTitleId()">
        <puro-typography
          class="address-form-section__title"
          variant="h2"
          component="h2"
          color="secondary"
        >
          {{ sectionTitleKey() | translate }}
        </puro-typography>
      </div>
      <p class="address-form-section__required-legend">
        <span class="address-form-section__required-mark" aria-hidden="true">*</span>
        {{ 'createShipment.requiredFieldLegend' | translate }}
      </p>
    </div>

    <puro-button
      type="button"
      variant="primary"
      class="address-form-section__clear"
      [attr.data-clear-section]="sectionKey()"
      (buttonClick)="onClearForm()"
    >
      {{ 'createShipment.actions.clearForm' | translate }}
    </puro-button>
  </div>

  @if (addressSuggestion(); as suggestion) {
    <app-address-suggested-address-panel
      class="address-form-section__suggested-address"
      [sectionRole]="suggestionRole()"
      [panelId]="suggestionPanelId()"
      [displayLine]="suggestion.displayLine"
      [mode]="suggestion.mode"
      (useSuggested)="onUseSuggestedAddress()"
      (keepEntry)="onKeepSuggestedAddressEntry()"
    />
  }

  <app-form-error-summary
    [title]="'createShipment.validation.errorSummaryTitle' | translate"
    [messages]="sectionErrorMessages()"
  />

  @if (showRuralRouteErrorBanner()) {
    <app-alert-toast class="address-form-section__rural-route-error" severity="error" live="polite">
      <p class="address-form-section__rural-route-error-title">
        {{ 'createShipment.validation.ruralRouteErrorBannerTitle' | translate }}
      </p>
      <ul class="address-form-section__rural-route-error-list">
        @if (ruralRouteState().civicAddressRequired) {
          <li>
            {{ 'createShipment.validation.ruralRouteErrorBannerPhysicalAddress' | translate }}
          </li>
        }
        @if (ruralRouteState().phoneRequired && sectionKey() !== 'shipTo') {
          <li>{{ ruralRoutePhoneBulletKey() | translate }}</li>
        }
      </ul>
    </app-alert-toast>
  }

  @if (showMissingUnitSuiteWarning()) {
    <app-alert-toast
      class="address-form-section__missing-unit-suite-warning"
      severity="warning"
      live="polite"
      [message]="'createShipment.validation.missingUnitSuiteWarning' | translate"
    />
  }

  <puro-checkbox
    class="address-form-section__notifications"
    formControlName="receiveNotifications"
    [label]="notificationsKey() | translate"
  />

  <div class="address-form-section__grid">
    <puro-input
      class="address-form-section__company"
      [label]="'createShipment.fields.companyOrFullName' | translate"
      formControlName="companyOrFullName"
      [name]="avsFieldName('companyOrFullName')"
      appDisableBrowserAutofill="disabled"
      [required]="true"
      [fullWidth]="true"
      [error]="!!fieldError('companyOrFullName')"
      [errorMessage]="fieldError('companyOrFullName') ?? ''"
      (focusout)="onFieldBlur('companyOrFullName')"
    />

    <puro-input
      class="address-form-section__attention"
      [label]="attentionLabelKey() | translate"
      formControlName="attentionTo"
      [name]="avsFieldName('attentionTo')"
      appDisableBrowserAutofill="disabled"
      [fullWidth]="true"
      [error]="!!fieldError('attentionTo')"
      [errorMessage]="fieldError('attentionTo') ?? ''"
      [endAdornment]="true"
      (focusout)="onFieldBlur('attentionTo')"
    >
      <span endAdornment class="address-form-section__attention-icon">
        <span class="visually-hidden" [id]="contactTooltipDescId()">{{
          attentionTooltipKey() | translate
        }}</span>
        <puro-tooltip #attentionTooltip [content]="attentionTooltipKey() | translate">
          <button
            type="button"
            class="address-form-section__attention-trigger"
            [attr.aria-label]="attentionTooltipAriaKey() | translate"
            [attr.aria-describedby]="contactTooltipDescId()"
            (click)="$event.preventDefault(); $event.stopPropagation()"
            (focus)="attentionTooltip.show()"
            (blur)="attentionTooltip.hide()"
          >
            <puro-icon name="info-circle-tooltip" size="sm" aria-hidden="true" />
          </button>
        </puro-tooltip>
      </span>
    </puro-input>

    <div class="address-form-section__street">
      <app-street-address-autocomplete-field
        class="address-form-section__street-field"
        [label]="'createShipment.fields.streetAddress' | translate"
        formControlName="streetAddress"
        [inputName]="avsFieldName('streetAddress')"
        [required]="true"
        [fullWidth]="true"
        [error]="!!fieldError('streetAddress')"
        [errorMessage]="fieldError('streetAddress') ?? ''"
        [describedByIds]="streetDescribedByIds()"
        [listboxLabel]="'createShipment.fields.streetAddressSuggestions' | translate"
        (suggestionSelected)="streetAddressSuggestionSelected.emit($event)"
        (focusout)="onFieldBlur('streetAddress')"
      />
      @if (showRuralRouteInfo()) {
        <p class="address-form-section__street-helper" aria-live="polite">
          {{ 'createShipment.validation.ruralRouteStreetHelper' | translate }}
        </p>
      }
    </div>

    <puro-input
      class="address-form-section__suite"
      [label]="'createShipment.fields.suiteAptUnit' | translate"
      formControlName="suiteAptUnit"
      [name]="avsFieldName('suiteAptUnit')"
      appDisableBrowserAutofill="disabled"
      [fullWidth]="true"
      [error]="!!fieldError('suiteAptUnit')"
      [errorMessage]="fieldError('suiteAptUnit') ?? ''"
      (focusout)="onFieldBlur('suiteAptUnit')"
    />

    <puro-input
      class="address-form-section__buzzer"
      [label]="'createShipment.fields.entryBuzzerCode' | translate"
      formControlName="entryBuzzerCode"
      [name]="avsFieldName('entryBuzzerCode')"
      appDisableBrowserAutofill="disabled"
      [fullWidth]="true"
      [error]="!!fieldError('entryBuzzerCode')"
      [errorMessage]="fieldError('entryBuzzerCode') ?? ''"
      (focusout)="onFieldBlur('entryBuzzerCode')"
    />

    <app-optional-address-lines
      [formGroup]="addressFormGroup"
      [expanded]="addressLinesExpanded()"
      [submitted]="submitted()"
      [avsNamePrefix]="sectionKey()"
      [ruralRouteDetected]="showRuralRouteInfo()"
      (expandRequested)="onExpandAddressLines()"
    />

    <puro-input
      class="address-form-section__city"
      [label]="'createShipment.fields.city' | translate"
      formControlName="city"
      [name]="avsFieldName('city')"
      appDisableBrowserAutofill="disabled"
      [required]="true"
      [fullWidth]="true"
      [error]="!!fieldError('city')"
      [errorMessage]="fieldError('city') ?? ''"
      (focusout)="onFieldBlur('city')"
    />

    <app-floating-select
      class="address-form-section__province"
      formControlName="province"
      [label]="'createShipment.fields.province' | translate"
      [options]="provinceOptions()"
      [required]="true"
      [fullWidth]="true"
      [error]="!!fieldError('province')"
      [errorMessage]="fieldError('province') ?? ''"
      (focusout)="onFieldBlur('province')"
    />

    <puro-input
      class="address-form-section__postal"
      [label]="'createShipment.fields.postalCode' | translate"
      formControlName="postalCode"
      [name]="avsFieldName('postalCode')"
      appDisableBrowserAutofill="disabled"
      [required]="true"
      [fullWidth]="true"
      [error]="!!fieldError('postalCode')"
      [errorMessage]="fieldError('postalCode') ?? ''"
      [endAdornment]="sectionKey() === 'shipFrom'"
      (focusout)="onFieldBlur('postalCode')"
    >
      @if (sectionKey() === 'shipFrom') {
        <span endAdornment class="address-form-section__attention-icon">
          <span class="visually-hidden" [id]="postalTooltipDescId()">{{
            'createShipment.fields.postalCodeTooltip' | translate
          }}</span>
          <puro-tooltip
            #postalTooltip
            [content]="'createShipment.fields.postalCodeTooltip' | translate"
          >
            <button
              type="button"
              class="address-form-section__attention-trigger"
              [attr.aria-label]="'createShipment.fields.postalCodeTooltipAria' | translate"
              [attr.aria-describedby]="postalTooltipDescId()"
              (click)="$event.preventDefault(); $event.stopPropagation()"
              (focus)="postalTooltip.show()"
              (blur)="postalTooltip.hide()"
            >
              <puro-icon name="info-circle-tooltip" size="sm" aria-hidden="true" />
            </button>
          </puro-tooltip>
        </span>
      }
    </puro-input>

    <puro-input
      class="address-form-section__email"
      [label]="'createShipment.fields.email' | translate"
      formControlName="email"
      [name]="avsFieldName('email')"
      appDisableBrowserAutofill="disabled"
      [required]="contactFieldsRequired()"
      [fullWidth]="true"
      [error]="!!fieldError('email')"
      [errorMessage]="fieldError('email') ?? ''"
      [endAdornment]="sectionKey() === 'shipFrom'"
      (focusout)="onFieldBlur('email')"
    >
      @if (sectionKey() === 'shipFrom') {
        <span endAdornment class="address-form-section__attention-icon">
          <span class="visually-hidden" [id]="emailTooltipDescId()">{{
            'createShipment.fields.emailTooltip' | translate
          }}</span>
          <puro-tooltip #emailTooltip [content]="'createShipment.fields.emailTooltip' | translate">
            <button
              type="button"
              class="address-form-section__attention-trigger"
              [attr.aria-label]="'createShipment.fields.emailTooltipAria' | translate"
              [attr.aria-describedby]="emailTooltipDescId()"
              (click)="$event.preventDefault(); $event.stopPropagation()"
              (focus)="emailTooltip.show()"
              (blur)="emailTooltip.hide()"
            >
              <puro-icon name="info-circle-tooltip" size="sm" aria-hidden="true" />
            </button>
          </puro-tooltip>
        </span>
      }
    </puro-input>

    <app-phone-field-group
      class="address-form-section__phone"
      [avsNamePrefix]="sectionKey()"
      [formGroup]="addressFormGroup"
      [phoneRequired]="contactFieldsRequired()"
      [submitted]="submitted()"
      [showPhoneTooltip]="sectionKey() === 'shipFrom'"
    />
  </div>

  @if (showReturnAddressToggle()) {
    <puro-checkbox
      class="address-form-section__return-toggle"
      [checked]="useDifferentReturnAddress()"
      (checkedChange)="onReturnAddressToggle($event)"
      [label]="'createShipment.sections.shipTo.useDifferentReturnAddress' | translate"
    />
  }
</section>
